import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useParams } from 'react-router';
import { api, ApiError } from '../api.js';
import AppHeader from '../components/AppHeader.js';
import CompetitionFlag from '../components/CompetitionFlag.js';
import { useActor } from '../lib/useActor.js';

/**
 * The bolão's own URL, which is now a gate rather than a destination.
 *
 * If you already play this bolão (or organize its circle) there is nothing
 * to decide here, so it redirects straight to Predictions — the thing people
 * actually come to do. Leaderboard and Rules are a tab away from there
 * (`PoolNav`) instead of being a menu you have to pass back through.
 *
 * If you're in the circle but haven't opted into this bolão, that choice is
 * what this screen is for: joining a circle deliberately doesn't join any
 * bolão in it, so this is where that separate, explicit step happens. The
 * form mirrors `JoinScreen`'s join-a-circle flow.
 */
export default function PoolScreen() {
  const { t } = useTranslation();
  const { id = '', poolId = '' } = useParams();
  const queryClient = useQueryClient();
  const { actor } = useActor();

  const [displayName, setDisplayName] = useState('');

  const {
    data: pool,
    isPending,
    isError,
  } = useQuery({ queryKey: ['pool', id, poolId], queryFn: () => api.getPool(id, poolId) });

  // Default to whatever this person is already called — their account name,
  // or the name a guest chose on the invite link. Read through `useActor`
  // rather than the Better Auth session so a guest gets a filled-in field too
  // instead of a blank one they have to retype.
  useEffect(() => {
    if (actor?.name && !displayName) setDisplayName(actor.name);
  }, [actor?.name, displayName]);

  const join = useMutation({
    mutationFn: () => api.joinPool(id, poolId, displayName.trim()),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['pool', id, poolId] });
      await queryClient.invalidateQueries({ queryKey: ['pools', id] });
    },
  });

  if (isPending) {
    return (
      <main className="screen">
        <p className="muted">{t('common.loading')}</p>
      </main>
    );
  }

  if (isError || !pool) {
    return (
      <main className="screen">
        <Link to={`/groups/${id}`} className="muted">
          {t('common.back')}
        </Link>
        <p className="error">{t('common.somethingWentWrong')}</p>
      </main>
    );
  }

  if (pool.isPlaying || pool.isOrganizer) {
    return <Navigate to={`/groups/${id}/bolaos/${poolId}/predictions`} replace />;
  }

  return (
    <main className="screen">
      <AppHeader groupId={id} />
      <Link to={`/groups/${id}`} className="muted">
        {t('common.back')}
      </Link>

      <div>
        <h1>{pool.name}</h1>
        <p className="muted">
          <CompetitionFlag slug={pool.competitionSlug} />
          {pool.competitionDisplayName}
        </p>
      </div>

      <form
        className="panel stack"
        style={{ padding: 'var(--space-4)' }}
        onSubmit={(event) => {
          event.preventDefault();
          if (displayName.trim() && !join.isPending) join.mutate();
        }}
      >
        <p>{t('pool.joinPrompt')}</p>
        <div>
          <label className="label" htmlFor="displayName">
            {t('join.nameLabel')}
          </label>
          <input
            id="displayName"
            className="input"
            value={displayName}
            maxLength={60}
            placeholder={t('join.namePlaceholder')}
            onChange={(event) => setDisplayName(event.target.value)}
          />
          <p className="muted" style={{ fontSize: '0.8rem', marginTop: 'var(--space-1)' }}>
            {t('join.nameHint')}
          </p>
        </div>

        {join.isError ? (
          <p className="error">
            {join.error instanceof ApiError && join.error.code === 'name_taken'
              ? t('join.nameTaken')
              : t('common.somethingWentWrong')}
          </p>
        ) : null}

        <button
          type="submit"
          className="button button-primary"
          disabled={!displayName.trim() || join.isPending}
        >
          {join.isPending ? t('join.joining') : t('pool.joinSubmit')}
        </button>
      </form>
    </main>
  );
}
